import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getMandirs, deleteMandir, createMandir, updateMandir } from "../../../utils/api";

const MEDIA_BASE_URL = process.env.REACT_APP_MEDIA_URL || (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/admin\/?$/, '') : 'https://www.sanatandharmmahasabha.com');

const serializePrayerDays = (prayerDays) => {
  if (!Array.isArray(prayerDays)) return "";
  return prayerDays
    .map((entry) => {
      const day = entry?.day || "";
      const times = Array.isArray(entry?.times)
        ? entry.times
            .map((time) => {
              const hour = String(time?.hour ?? "").padStart(2, "0");
              const minute = String(time?.minute ?? "").padStart(2, "0");
              return hour && minute ? `${hour}:${minute}` : "";
            })
            .filter(Boolean)
            .join(", ")
        : "";
      return day ? `${day}: ${times}`.trim() : "";
    })
    .filter(Boolean)
    .join("\n");
};

const parsePrayerDays = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf(":");
      const day = (separatorIndex === -1 ? line : line.slice(0, separatorIndex)).trim();
      const timePart = separatorIndex === -1 ? "" : line.slice(separatorIndex + 1);
      const times = timePart
        .split(",")
        .map((time) => time.trim())
        .filter(Boolean)
        .map((time) => {
          const [hour, minute] = time.split(":").map(Number);
          if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
          return { hour, minute };
        })
        .filter(Boolean);

      return day ? { day, times } : null;
    })
    .filter(Boolean);

const columns = [
  {
    key: "image",
    label: "Image",
    render: (value) => {
      const isBase64 = typeof value === 'string' && value.startsWith('data:');
      const imageUrl = value
        ? isBase64
          ? value
          : value.startsWith('http')
            ? value
            : `${MEDIA_BASE_URL}/media/${value}`
        : null;

      return imageUrl ? (
        <div className="image-bubble">
          <img src={imageUrl} alt="Mandir" />
        </div>
      ) : (
        <div className="image-bubble placeholder">
          <span>🏛️</span>
        </div>
      );
    }
  },
  { key: "name", label: "Name" },
  { key: "address", label: "Address" },
  { key: "area", label: "Area" },
  {
    key: "headResponsible",
    label: "Head Responsible",
    render: (value) => {
      if (!value) return "-";
      if (typeof value === 'string') return value;
      if (typeof value === 'object') {
        const parts = [value.name, value.phone].filter(Boolean);
        return parts.length ? parts.join(' - ') : "-";
      }
      return "-";
    }
  },
  {
    key: "buildDate",
    label: "Build Date",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
  {
    key: "createdOn",
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "area",
    label: "Filter by Area",
    type: "search",
    placeholder: "Search area"
  },
];

export default function Mandirs() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedMandir, setSelectedMandir] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    address: "",
    area: "",
    buildDate: "",
    headResponsibleName: "",
    headResponsiblePhone: "",
    prayerDaysText: "",
    image: "" // Base64 or URL
  });
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData({
      name: "",
      description: "",
      address: "",
      area: "",
      buildDate: "",
      headResponsibleName: "",
      headResponsiblePhone: "",
      prayerDaysText: "",
      image: ""
    });
    setIsEditMode(false);
    setSelectedMandir(null);
    setModalOpen(true);
  };

  const handleEdit = (mandir) => {
    setSelectedMandir(mandir);
    const headResponsibleValue = mandir.headResponsible
      ? typeof mandir.headResponsible === 'string'
        ? mandir.headResponsible
        : mandir.headResponsible.name || ''
      : '';

    setFormData({
      name: mandir.name || "",
      description: mandir.description || "",
      address: mandir.address || "",
      area: mandir.area || "",
      buildDate: mandir.buildDate ? new Date(mandir.buildDate).toISOString().split('T')[0] : "",
      headResponsibleName: headResponsibleValue,
      headResponsiblePhone: mandir.headResponsible?.phone || "",
      prayerDaysText: serializePrayerDays(mandir.prayerDays),
      image: mandir.image || ""
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (mandir) => {
    handleEdit(mandir);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, image: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      image: formData.image,
      name: formData.name.trim(),
      description: formData.description.trim(),
      address: formData.address.trim(),
      area: formData.area.trim(),
      buildDate: formData.buildDate || undefined,
      headResponsible: {
        name: formData.headResponsibleName.trim(),
        phone: formData.headResponsiblePhone.trim(),
      },
      prayerDays: parsePrayerDays(formData.prayerDaysText),
    };

    try {
      if (isEditMode && selectedMandir) {
        await updateMandir(selectedMandir._id, payload);
        alert("Mandir updated successfully!");
      } else {
        await createMandir(payload);
        alert("Mandir created successfully!");
      }
      setModalOpen(false);
    } catch (error) {
      alert(error.message || "Failed to save mandir");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Mandirs"
          fetchData={getMandirs}
          deleteData={deleteMandir}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search mandirs..."
        />
      </main>

      {/* Create/Edit Mandir Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Mandir" : "Add New Mandir"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Mandir Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                required
                placeholder="Enter mandir name"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-group">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleFormChange}
                required
                placeholder="Enter full address"
              />
            </div>
            <div className="form-group">
              <label>Area</label>
              <input
                type="text"
                name="area"
                value={formData.area}
                onChange={handleFormChange}
                placeholder="Type area, district or region"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Build Date</label>
                <input
                  type="date"
                  name="buildDate"
                  value={formData.buildDate}
                  onChange={handleFormChange}
                />
              </div>
              <div className="form-group">
                <label>Head Responsible</label>
                <input
                  type="text"
                  name="headResponsibleName"
                  value={formData.headResponsibleName}
                  onChange={handleFormChange}
                  placeholder="Enter name"
                />
              </div>
              <div className="form-group">
                <label>Head Responsible Phone</label>
                <input
                  type="text"
                  name="headResponsiblePhone"
                  value={formData.headResponsiblePhone}
                  onChange={handleFormChange}
                  placeholder="Enter phone number"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Prayer Days</label>
              <textarea
                name="prayerDaysText"
                value={formData.prayerDaysText}
                onChange={handleFormChange}
                rows="4"
                placeholder={"One line per day, for example:\nMonday: 19:00, 20:30\nFriday: 18:00"}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Image</h3>
            <div className="form-group">
              <label>Mandir Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.image && (
                <div className="image-preview">
                  <img src={formData.image} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, image: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update Mandir" : "Create Mandir")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
